<template>
    <div class="course__edit_links_form">
        <div class="columns is-variable">
            <div class="column is-6">
                <div class="course__edit_links_form_header">
                    <div class="columns is-vcentered">
                        <div class="column">
                            <component-title
                                :class="'course__edit_links_form_title'"
                                :size="'small'"
                                :raw="true"
                            >Useful links
                            </component-title>
                        </div>
                        <div class="column is-narrow">
                            <component-button
                                :name="'course-edit-links-form-add'"
                                :theme="'default'"
                                @click="handleAddNewUrl"
                            >Add link
                            </component-button>
                        </div>
                    </div>
                </div>
                <div class="course__edit_links_form_main">
                    <div class="course__edit_links_form_main_urls">
                        <template v-for="(url, index) of existingUrls">
                            <div
                                :key="index"
                                class="course__edit_links_form_main_urls_item"
                            >
                                <div class="columns is-variable">
                                    <div class="column">
                                        <div class="course__edit_links_form_main_urls_item_text">{{ url.text }}</div>
                                        <div class="course__edit_links_form_main_urls_item_url">
                                            <div class="columns is-vcentered">
                                                <div class="column">
                                                    <a
                                                        :class="'course__edit_links_form_main_urls_item_url_button'"
                                                        :name="'course-edit-links-form-main-urls-item-url-button'"
                                                        :href="(!url.url.includes('http') ? '//' : '') + url.url"
                                                        target="_blank"
                                                    >{{ url.url }}</a>
                                                </div>
                                                <div class="column is-narrow">
                                                    <component-button
                                                        :class="'course__edit_links_form_main_urls_item_url_copy'"
                                                        :name="'course-edit-links-form-main-urls-item-url-copy'"
                                                        :icon="true"
                                                        :icon-name="'link'"
                                                        :icon-raw="true"
                                                        :theme="''"
                                                        @click="handleCopyToClipboard(url.url)"
                                                    ></component-button>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="course__edit_links_form_main_urls_item_visibility">
                                            Visible to:
                                            <template v-if="url.visibility === 'all'">
                                                All learners
                                            </template>
                                            <template v-else-if="url.visibility === 'enrolled'">
                                                Enrolled learners only
                                            </template>
                                            <template v-else>
                                                None
                                            </template>
                                        </div>
                                        <template v-if="url.providedByCourse && isCatalogItem">
                                            <div class="course__edit_links_form_main_urls_item_providedbycourse">
                                                Provided by course
                                            </div>
                                        </template>
                                    </div>
                                    <div class="column is-narrow">
                                        <div class="course__edit_links_form_main_urls_item_actions">
                                            <div class="columns is-variable">
                                                <div class="column">
                                                    <component-button
                                                        :class="'course__edit_links_form_main_urls_item_url_actions_edit'"
                                                        :name="'course-edit-links-form-main-urls-item-url-actions-edit'"
                                                        :icon="true"
                                                        :icon-name="'settings'"
                                                        :theme="'default'"
                                                        @click="handleEditUrl({
                                                            url: url.url,
                                                            text: url.text,
                                                            visibility: url.visibility,
                                                            providedByCourse: url.providedByCourse
                                                        })"
                                                    ></component-button>
                                                </div>
                                                <template v-if="!url.providedByCourse">
                                                    <div class="column">
                                                        <component-button
                                                            :class="'course__edit_links_form_main_urls_item_url_actions_remove'"
                                                            :name="'course-edit-links-form-main-urls-item-url-actions-remove'"
                                                            :icon="true"
                                                            :icon-name="'trash-2'"
                                                            :theme="'default'"
                                                            @click="handleRemoveUrl(url.url)"
                                                        ></component-button>
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <pop-notify
            ref="linkformnotify"
            :title-text="`${toCapitalize(mode)} link`"
            :content-text="''"
            :cancel-button-active="false"
            :submit-button-active="false"
            @emit-close="clearData"
        >
            <form-text
                :disabled="(providedByCourse && isCatalogItem) || mode === 'edit'"
                :label="'URL'"
                :name="'course-edit-links-form-name'"
                :placeholder="'Insert URL here'"
                :required="true"
                :v-validate="{ excluded: listUrls, required: true, regex: /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/ }"
                :data-vv-as="'url'"
                v-model="url"
            ></form-text>
            <form-text
                :disabled="providedByCourse && isCatalogItem"
                :label="'Text to display'"
                :name="'course-edit-links-form-text'"
                :placeholder="'Insert text to display'"
                :required="true"
                :v-validate="{ required: true }"
                :data-vv-as="'text'"
                v-model="text"
            >This text will be displayed as the link.
            </form-text>
            <form-radio
                :label="'Display to'"
                :name="'course-edit-links-form-displayto'"
                :items="visibilityItems"
                v-model="visibility"
            ></form-radio>
            <div class="columns is-variable">
                <div class="column"></div>
                <div class="column is-narrow">
                    <component-button
                        :name="'course-edit-links-form-cancel'"
                        :theme="'default'"
                        @click="closeNotify"
                    >Cancel
                    </component-button>
                </div>
                <div class="column is-narrow">
                    <component-button
                        :name="'course-edit-links-form-submit'"
                        @click="handleSubmit"
                    >{{mode === 'edit' ? 'Save link' : 'Add link' }}
                    </component-button>
                </div>
            </div>
        </pop-notify>
    </div>
</template>

<script>
import { validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import ComponentButton from '@/components/default/shared/ComponentButton'
import PopNotify from '@/components/default/shared/PopNotify'
import FormText from '@/components/default/forms/FormText'
import FormRadio from '@/components/default/forms/FormRadio'

export default {
  name: 'course-edit-links-form',
  components: {
    FormRadio,
    FormText,
    PopNotify,
    ComponentButton,
    ComponentTitle
  },
  mixins: [validateBeforeRouteLeave, watchContentChangeEmit],
  data () {
    return {
      url: '',
      text: '',
      visibility: 'all',
      providedByCourse: false,
      mode: 'add',
      rerenderIndex: 0
    }
  },
  props: {
    isCourse: {
      type: Boolean,
      default: true
    },
    isCatalogItem: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    visibilityItems () {
      let itemsArr = []

      itemsArr.push({
        name: 'All learners',
        value: 'all'
      })
      itemsArr.push({
        name: 'Enrolled learners only',
        value: 'enrolled'
      })
      if (this.mode === 'edit') {
        itemsArr.push({
          name: 'Do not display',
          value: 'none'
        })
      }

      return itemsArr
    },
    getUrlsGetterNameCourse () {
      return 'getEbsCourseUrls'
    },
    getUrlsGetterNameCatalogItem () {
      return 'getEbsCatalogItemUrls'
    },
    addUrlDispatchName () {
      return this.isCatalogItem ? 'ebsCatalogItemAddUrl' : 'ebsCourseAddUrl'
    },
    updateUrlDispatchName () {
      return this.isCatalogItem ? 'ebsCatalogItemUpdateUrl' : 'ebsCourseUpdateUrl'
    },
    removeUrlDispatchName () {
      return this.isCatalogItem ? 'ebsCatalogItemRemoveUrl' : 'ebsCourseRemoveUrl'
    },
    existingUrls () {
      const courseUrls = this.$store.getters[this.getUrlsGetterNameCourse]
      const courseUrlsMapped = courseUrls.length ? courseUrls.map(obj => {
        return {
          text: obj.text,
          url: obj.url,
          visibility: obj.visibility,
          providedByCourse: this.isCatalogItem
        }
      }) : []
      const catalogItemUrls = this.$store.getters[this.getUrlsGetterNameCatalogItem]

      return this.isCatalogItem ? catalogItemUrls : courseUrlsMapped
    },
    listUrls () {
      return this.existingUrls.length ? this.existingUrls.map(obj => obj.url) : []
    }
  },
  methods: {
    clearData () {
      this.url = ''
      this.text = ''
      this.visibility = 'all'
      this.providedByCourse = false
      this.mode = 'add'
    },
    closeNotify () {
      this.$refs.linkformnotify.closeNotify()
    },
    openNotify () {
      this.$refs.linkformnotify.openNotify()
    },
    handleAddNewUrl () {
      this.openNotify()
    },
    handleEditUrl (data) {
      this.url = data.url
      this.text = data.text
      this.visibility = data.visibility
      this.providedByCourse = data.providedByCourse
      this.mode = 'edit'
      this.openNotify()
    },
    handleRemoveUrl (data) {
      this.$store.dispatch(this.removeUrlDispatchName, data)
    },
    handleSubmit () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.mode === 'add') {
            this.$store.dispatch(this.addUrlDispatchName, { text: this.text, url: this.url, visibility: this.visibility, providedByCourse: this.providedByCourse })
          } else if (this.mode === 'edit') {
            this.$store.dispatch(this.updateUrlDispatchName, { text: this.text, url: this.url, visibility: this.visibility, providedByCourse: this.providedByCourse })
          }
          this.closeNotify()
          // this.rerenderIndex++
        }
      })
    },
    handleCopyToClipboard (text) {
      var input = document.createElement('input')
      input.setAttribute('value', text)
      document.body.appendChild(input)
      input.select()
      var result = document.execCommand('copy')
      document.body.removeChild(input)

      this.$store.dispatch('defaultNotificationToastsAdd', {
        title: 'Link copy',
        message: 'Link has been successfully copied.',
        status: 'success'
      })

      return result
    }
  },
  inject: ['$validator'],
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/courses/courseaddeditlinksform";
</style>
